/**
 * Quick Actions Panel - Action buttons for daily operations
 * Epic 61: Admin Kozpont Dashboard (Story 61-2)
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useNavigate } from 'react-router-dom';

interface QuickActionsPanelProps {
  onDailyOpen: () => void;
  onDailyClose: () => void;
}

interface QuickActionButtonProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  colorClass: string;
}

function QuickActionButton({
  icon,
  label,
  description,
  onClick,
  colorClass,
}: QuickActionButtonProps) {
  return (
    <Card
      className="cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] hover:ring-2 hover:ring-primary/20"
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-4 p-5">
        <div
          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${colorClass}`}
        >
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{label}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function QuickActionsPanel({ onDailyOpen, onDailyClose }: QuickActionsPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div>
      <CardHeader className="px-0">
        <CardTitle className="text-lg">{t('admin.quickActions')}</CardTitle>
      </CardHeader>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* 1. Napi megnyitas */}
        <QuickActionButton
          icon={
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          }
          label={t('admin.dailyOpen')}
          description={t('admin.dailyOpenDesc')}
          onClick={onDailyOpen}
          colorClass="bg-green-500"
        />

        {/* 2. Napi zaras */}
        <QuickActionButton
          icon={
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          }
          label={t('admin.dailyClose')}
          description={t('admin.dailyCloseDesc')}
          onClick={onDailyClose}
          colorClass="bg-indigo-500"
        />

        {/* 3. Emlekezteto kuldes */}
        <QuickActionButton
          icon={
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          }
          label={t('admin.sendReminders')}
          description={t('admin.sendRemindersDesc')}
          onClick={() => navigate('/rental?status=OVERDUE')}
          colorClass="bg-amber-500"
        />

        {/* 4. Tudasbazis */}
        <QuickActionButton
          icon={
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          }
          label={t('admin.knowledgeBase')}
          description={t('admin.knowledgeBaseDesc')}
          onClick={() => navigate('/admin-center/kb')}
          colorClass="bg-primary"
        />

        {/* 5. pRI Asszisztens Beállítások */}
        <QuickActionButton
          icon={
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 00.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M19 14.5l-2.47 2.47a2.25 2.25 0 01-1.59.659H9.06a2.25 2.25 0 01-1.591-.659L5 14.5m14 0V17a2 2 0 01-2 2H7a2 2 0 01-2-2v-2.5"
              />
            </svg>
          }
          label="pRI Asszisztens"
          description="LLM beállítások, rate limitek, kvóták"
          onClick={() => navigate('/admin-center/pri-settings')}
          colorClass="bg-violet-500"
        />
      </div>
    </div>
  );
}
